// src/rms/pages/Certifications.js
// ============================================================================
// Operator certifications — construction equipment
// ============================================================================
// The certification gate is real: checkout is blocked unless the account holds
// a verified, unexpired certificate matching the machine's required type. This
// screen is where that evidence is recorded and verified.
// ============================================================================

import React, { useMemo, useState } from "react";
import db from "../data";
import { useRms } from "../context/RmsContext";
import { useCollection, indexById } from "../hooks/useCollection";
import { optionsForField } from "../verticals/presets";
import { optionLabel } from "../i18n/optionLabel";
import { PageHeader, Stat, StatusPill, Modal, Field, TextInput, Select, Banner, EmptyState, formatDate } from "../components/ui";
import adEngine from "../ads/adEngine";

// The certification list is NOT declared here. It is the option list on the
// vertical's own required_certification field, and keeping a second copy in
// this file is what let the two drift apart in the first place.

function Certifications() {
  const { operator, config, t, tLower, invalidate } = useRms();
  const { rows: certifications } = useCollection("certifications", {}, { orderBy: "expires_at" });
  const { rows: customers } = useCollection("customers");
  const { rows: assets } = useCollection("assets");

  const [editing, setEditing] = useState(null);

  const customersById = useMemo(() => indexById(customers), [customers]);
  const today = new Date().toISOString().slice(0, 10);

  const withState = useMemo(
    () =>
      certifications.map((c) => ({
        ...c,
        expired: c.expires_at && c.expires_at < today,
        expiringSoon:
          c.expires_at && c.expires_at >= today && c.expires_at <= new Date(Date.now() + 60 * 864e5).toISOString().slice(0, 10),
      })),
    [certifications, today]
  );

  const verified = withState.filter((c) => c.status === "verified" && !c.expired);
  const expiring = withState.filter((c) => c.expiringSoon && c.status === "verified");
  const expired = withState.filter((c) => c.expired);

  // Every certification this vertical knows about, straight from its schema.
  const certOptions = useMemo(
    () => optionsForField(config, "required_certification"),
    [config]
  );

  // "none" is a real stored value meaning the machine needs no ticket, so it is
  // never offered as something a person can hold.
  const certTypes = useMemo(
    () => certOptions.filter((o) => o.value !== "none"),
    [certOptions]
  );

  // Which certifications the current fleet actually demands.
  const requiredTypes = useMemo(
    () =>
      Array.from(
        new Set(
          assets
            .map((a) => a.custom_fields?.required_certification)
            .filter((x) => x && x !== "none")
        )
      ),
    [assets]
  );

  const save = async (draft) => {
    adEngine.recordAction();
    const payload = {
      operator_id: operator.id,
      customer_id: draft.customer_id,
      certification_type: draft.certification_type,
      holder_name: draft.holder_name,
      certificate_number: draft.certificate_number || null,
      issuing_authority: draft.issuing_authority || null,
      issued_at: draft.issued_at || null,
      expires_at: draft.expires_at || null,
      status: draft.status,
      verified_at: draft.status === "verified" ? new Date().toISOString() : null,
    };
    if (draft.id) await db.update("certifications", draft.id, payload);
    else await db.create("certifications", payload);
    setEditing(null);
    invalidate();
  };

  return (
    <>
      <PageHeader
        title={t("certifications.certifications", "Certifications")}
        subtitle={t("certifications.operatorTicketsOnFile", "Operator tickets on file. Checkout is blocked without a valid one.")}
        actions={
          <button type="button" className="rms-btn rms-btn--primary" onClick={() => setEditing(blank())}>
            {t("certifications.record", "Record a certificate")}
          </button>
        }
      />

      {expired.length ? (
        <Banner tone="danger">
          <strong>{expired.length} expired.</strong>
          {t("certifications.anyAccountRelyingOn", "Any account relying on these will be blocked at checkout until a current certificate is recorded.")}
        </Banner>
      ) : null}

      {expiring.length ? (
        <Banner tone="warn">
          <strong>{expiring.length} expiring within 60 days.</strong>
          {t("certifications.worthChasingBeforeThe", "Worth chasing before the next booking.")}
        </Banner>
      ) : null}

      <div className="rms-stat-row">
        <Stat label={t("certifications.valid", "Valid")} value={verified.length} />
        <Stat label={t("certifications.expiringSoon", "Expiring soon")} value={expiring.length} hint="within 60 days" />
        <Stat label={t("certifications.expired", "Expired")} value={expired.length} />
        <Stat
          label={t("certifications.typesYourFleetNeeds", "Types your fleet needs")}
          value={requiredTypes.length}
          hint={requiredTypes.map((v) => optionLabel(certOptions, v)).join(", ") || t("common.none", "none")}
        />
      </div>

      {certifications.length === 0 ? (
        <EmptyState
          title={t("certifications.noCertificationsOnFile", "No certifications on file")}
          message={t("certifications.empty.body", "Record an operator's ticket against an {account} so they can take out certified machines.", {
            account: tLower("customer", "account"),
          })}
          action={
            <button type="button" className="rms-btn rms-btn--primary" onClick={() => setEditing(blank())}>
              {t("certifications.record", "Record a certificate")}
            </button>
          }
        />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>{t("accessLog.holder", "Holder")}</th>
                <th>{t("customer", "Account")}</th>
                <th>{t("certifications.certification", "Certification")}</th>
                <th>{t("certifications.number", "Number")}</th>
                <th>{t("certifications.expires", "Expires")}</th>
                <th>{t("campaignForm.status", "Status")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {withState.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.holder_name}</strong>
                  </td>
                  <td>
                    {customersById[c.customer_id]
                      ? `${customersById[c.customer_id].first_name} ${customersById[c.customer_id].last_name || ""}`.trim()
                      : "—"}
                  </td>
                  <td>{optionLabel(certOptions, c.certification_type)}</td>
                  <td style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12.5 }}>
                    {c.certificate_number || "—"}
                  </td>
                  <td style={{ fontSize: 12.5, color: c.expired ? "var(--rms-danger)" : c.expiringSoon ? "var(--rms-warn)" : undefined, fontWeight: c.expired || c.expiringSoon ? 650 : undefined }}>
                    {formatDate(c.expires_at)}
                  </td>
                  <td>
                    <StatusPill
                      status={c.expired ? "overdue" : c.status}
                      label={c.expired ? "expired" : c.status}
                    />
                  </td>
                  <td className="rms-num">
                    <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => setEditing(toDraft(c))}>
                      {t("action.edit", "Edit")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing ? (
        <CertForm draft={editing} customers={customers} certTypes={certTypes} t={t} onCancel={() => setEditing(null)} onSave={save} />
      ) : null}
    </>
  );
}

function CertForm({ draft: initial, customers, certTypes, t, onCancel, onSave }) {
  const { tLower } = useRms();
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState(null);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const submit = () => {
    if (!draft.customer_id) return setError(t("certifications.error.account", "Choose an {account}.", {
      account: tLower("customer", "account"),
    }));
    if (!draft.holder_name.trim()) return setError("Who holds this certificate?");
    if (!draft.certification_type) return setError("Choose a certification type.");
    setError(null);
    onSave(draft);
  };

  return (
    <Modal
      title={draft.id ? t("certifications.edit", "Edit certificate") : t("certifications.record", "Record a certificate")}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="rms-btn rms-btn--ghost" onClick={onCancel}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="button" className="rms-btn rms-btn--primary" onClick={submit}>
            {t("action.save", "Save")}
          </button>
        </>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}

      <div className="rms-field-grid">
        <Field label={t("customer", "Account")} required>
          <Select
            value={draft.customer_id}
            onChange={(v) => set("customer_id", v)}
            placeholder="Choose…"
            options={customers.map((c) => ({ value: c.id, label: `${c.first_name} ${c.last_name || ""}`.trim() }))}
          />
        </Field>
        <Field label={t("certifications.holderName", "Holder name")} required help={t("certifications.theIndividualOperatorWho", "The individual operator, who may not be the account holder.")}>
          <TextInput value={draft.holder_name} onChange={(v) => set("holder_name", v)} />
        </Field>
        <Field label={t("certifications.certification", "Certification")} required>
          <Select value={draft.certification_type} onChange={(v) => set("certification_type", v)} placeholder="Choose…" options={certTypes} />
        </Field>
        <Field label={t("certifications.certificateNumber", "Certificate number")}>
          <TextInput value={draft.certificate_number} onChange={(v) => set("certificate_number", v)} />
        </Field>
        <Field label={t("certifications.issuingAuthority", "Issuing authority")}>
          <TextInput value={draft.issuing_authority} onChange={(v) => set("issuing_authority", v)} />
        </Field>
        <Field label={t("certifications.issued", "Issued")}>
          <input className="rms-input" type="date" value={draft.issued_at || ""} onChange={(e) => set("issued_at", e.target.value)} />
        </Field>
        <Field label={t("certifications.expires", "Expires")} help={t("certifications.leaveBlankIfThe", "Leave blank if the certificate does not expire.")}>
          <input className="rms-input" type="date" value={draft.expires_at || ""} onChange={(e) => set("expires_at", e.target.value)} />
        </Field>
        <Field label={t("certifications.status", "Status")}>
          <Select
            value={draft.status}
            onChange={(v) => set("status", v)}
            placeholder={t("certifications.pending", "Pending")}
            options={[
              { value: "pending", label: "Pending review", labelKey: "opt.certStatus.pending" },
              { value: "verified", label: "Verified — document seen", labelKey: "opt.certStatus.verified" },
              { value: "rejected", label: "Rejected", labelKey: "opt.certStatus.rejected" },
            ]}
          />
        </Field>
      </div>

      <Banner tone="info">
        {t("certifications.onlyA", "Only a")} <strong>verified</strong>, unexpired certificate satisfies the checkout gate.
      </Banner>
    </Modal>
  );
}

function blank() {
  return {
    id: null, customer_id: "", holder_name: "", certification_type: "",
    certificate_number: "", issuing_authority: "", issued_at: "", expires_at: "", status: "pending",
  };
}

function toDraft(c) {
  return {
    id: c.id, customer_id: c.customer_id, holder_name: c.holder_name,
    certification_type: c.certification_type, certificate_number: c.certificate_number || "",
    issuing_authority: c.issuing_authority || "", issued_at: c.issued_at || "",
    expires_at: c.expires_at || "", status: c.status,
  };
}

export default Certifications;
